/**
 * useInvestigation — React Query hooks for investigation endpoints.
 *
 * Provides hooks for listing investigations, fetching detail, and
 * accessing sub-resources (evidence, claims, counter-signals, drift, certificate).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { osintApi, type OsintSignal } from '../api/osint';
import {
  fetchInvestigations,
  fetchEvidence,
  fetchClaims,
  fetchCounterSignals,
  fetchDrift,
  fetchCertificate,
  createInvestigation,
  submitEvidence,
  submitClaim,
  submitCounterSignal,
  submitDrift,
  fetchReadiness,
  buildCertificate,
  runAdmission,
  runShadowBootstrap,
  runShadowExecution,
  promoteLive,
  materializeTheatre,
  dismissInvestigation,
  reactivateInvestigation,
  acknowledgeDrift,
} from '../api/investigation';
import type {
  InvestigationSummary,
  EvidenceEnvelopeManifest,
  ClaimGraphSummary,
  CounterSignal,
  DriftEvent,
  CertificateRecordResponse,
  EvidenceSubmitRequest,
  ClaimCreateRequest,
  CounterSignalCreateRequest,
  DriftCreateRequest,
} from '../types/investigation';

// ── List ─────────────────────────────────────────────────────────────────

/**
 * @param options.pollInterval — Override the default 15s poll cadence.
 *   Pass a slower value when the list panel is hidden, or `false` to stop polling.
 */
export function useInvestigationList(options?: { pollInterval?: number | false }) {
  const query = useQuery({
    queryKey: ['investigations'],
    queryFn: fetchInvestigations,
    staleTime: 10_000,
    refetchInterval: options?.pollInterval ?? 15_000,
  });

  return {
    investigations: (query.data?.investigations ?? []) as InvestigationSummary[],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

// ── Investigation-scoped signals ─────────────────────────────────────────

/**
 * @param options.pollInterval — Override the default 30s poll cadence.
 *   Pass `false` to stop polling when the investigation surface is hidden.
 */
export function useInvestigationSignals(
  investigationId: string | null,
  options?: { pollInterval?: number | false; limit?: number },
) {
  const limit = options?.limit ?? 100;
  const query = useQuery({
    queryKey: ['osint', 'signals', { investigation_id: investigationId, limit }],
    queryFn: ({ signal }) =>
      osintApi.getSignals({ investigation_id: investigationId!, limit }, signal),
    enabled: !!investigationId,
    staleTime: 15_000,
    refetchInterval: options?.pollInterval ?? 30_000,
  });

  const signals = (query.data?.signals ?? []) as OsintSignal[];
  // #750: a capped server count arrives as total=null with
  // total_is_exact=false; it is never shown as an exact total.
  const totalIsExact = query.data?.total_is_exact ?? true;
  const totalCount = totalIsExact ? (query.data?.total ?? signals.length) : null;

  return {
    signals,
    totalCount,
    totalIsExact,
    totalLowerBound: query.data?.total_lower_bound ?? signals.length,
    isTruncated: totalCount === null || signals.length < totalCount,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ── Evidence ─────────────────────────────────────────────────────────────

export function useEvidence(investigationId: string | null) {
  const query = useQuery({
    queryKey: ['investigation', investigationId, 'evidence'],
    queryFn: () => fetchEvidence(investigationId!),
    enabled: !!investigationId,
    staleTime: 5_000,
  });

  return {
    evidence: (query.data ?? null) as EvidenceEnvelopeManifest | null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ── Claims ───────────────────────────────────────────────────────────────

export function useClaims(investigationId: string | null) {
  const query = useQuery({
    queryKey: ['investigation', investigationId, 'claims'],
    queryFn: () => fetchClaims(investigationId!),
    enabled: !!investigationId,
    staleTime: 5_000,
  });

  return {
    claims: (query.data ?? null) as ClaimGraphSummary | null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ── Counter-Signals ──────────────────────────────────────────────────────

export function useCounterSignals(investigationId: string | null) {
  const query = useQuery({
    queryKey: ['investigation', investigationId, 'counter-signals'],
    queryFn: () => fetchCounterSignals(investigationId!),
    enabled: !!investigationId,
    staleTime: 5_000,
  });

  return {
    signals: (query.data?.signals ?? []) as CounterSignal[],
    summary: (query.data?.summary ?? {}) as Record<string, number>,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ── Drift ────────────────────────────────────────────────────────────────

export function useDrift(investigationId: string | null) {
  const query = useQuery({
    queryKey: ['investigation', investigationId, 'drift'],
    queryFn: () => fetchDrift(investigationId!),
    enabled: !!investigationId,
    staleTime: 5_000,
  });

  return {
    events: (query.data?.events ?? []) as DriftEvent[],
    hasMaterialDrift: query.data?.has_material_drift ?? false,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ── Certificate ──────────────────────────────────────────────────────────

export function useCertificate(investigationId: string | null) {
  const query = useQuery({
    queryKey: ['investigation', investigationId, 'certificate'],
    queryFn: async () => {
      try {
        return await fetchCertificate(investigationId!);
      } catch (err) {
        // 404 = no certificate yet — normal investigation state, not an error
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!investigationId,
    staleTime: 30_000,
  });

  return {
    certificate: (query.data ?? null) as CertificateRecordResponse | null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ── Create ──────────────────────────────────────────────────────────────

export function useCreateInvestigation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createInvestigation,
    onSuccess: (created) => {
      // Seed the list cache immediately so InvestigationDrawer can mount
      // from `openInvestigation(id)` without waiting on the GET refetch.
      // invalidateQueries still runs so a later poll cannot drop the row
      // only because the POST handler shadowed the list fixture.
      queryClient.setQueryData(
        ['investigations'],
        (prev: { investigations?: InvestigationSummary[]; total?: number } | undefined) => {
          const existing = prev?.investigations ?? [];
          if (existing.some((inv) => inv.id === created.id)) {
            return {
              investigations: existing.map((inv) => (inv.id === created.id ? created : inv)),
              total: prev?.total ?? existing.length,
            };
          }
          const investigations = [created, ...existing];
          return { investigations, total: investigations.length };
        },
      );
      queryClient.invalidateQueries({ queryKey: ['investigations'] });
    },
  });
}

// ── Status transitions ──────────────────────────────────────────────────

export function useDismissInvestigation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (investigationId: string) => dismissInvestigation(investigationId),
    onSuccess: (_, investigationId) => {
      queryClient.invalidateQueries({ queryKey: ['investigations'] });
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
    },
  });
}

export function useReactivateInvestigation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (investigationId: string) => reactivateInvestigation(investigationId),
    onSuccess: (_, investigationId) => {
      queryClient.invalidateQueries({ queryKey: ['investigations'] });
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
    },
  });
}

// ── Submit Evidence ─────────────────────────────────────────────────────

export function useSubmitEvidence(investigationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: EvidenceSubmitRequest) => submitEvidence(investigationId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId, 'evidence'] });
    },
  });
}

// ── Submit Claim ────────────────────────────────────────────────────────

export function useSubmitClaim(investigationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ClaimCreateRequest) => submitClaim(investigationId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId, 'claims'] });
    },
  });
}

// ── Submit Counter-Signal ───────────────────────────────────────────────

export function useSubmitCounterSignal(investigationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CounterSignalCreateRequest) => submitCounterSignal(investigationId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
      queryClient.invalidateQueries({
        queryKey: ['investigation', investigationId, 'counter-signals'],
      });
    },
  });
}

// ── Submit Drift ────────────────────────────────────────────────────────

export function useSubmitDrift(investigationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: DriftCreateRequest) => submitDrift(investigationId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId, 'drift'] });
    },
  });
}

// ── Acknowledge Drift ──────────────────────────────────────────────────

export function useAcknowledgeDrift(investigationId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (driftId: string) =>
      acknowledgeDrift(investigationId!, driftId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['investigation', investigationId, 'drift'],
      });
    },
  });
}

// ── Readiness ───────────────────────────────────────────────────────────

/**
 * @param options.pollInterval — Override the default 10s poll cadence.
 *   Pass `false` to stop polling when the proof dock tab is not active.
 */
export function useReadiness(
  investigationId: string | null,
  options?: { pollInterval?: number | false },
) {
  const query = useQuery({
    queryKey: ['investigation', investigationId, 'readiness'],
    queryFn: () => fetchReadiness(investigationId!),
    enabled: !!investigationId,
    staleTime: 5_000,
    refetchInterval: options?.pollInterval ?? 10_000,
  });

  return {
    readiness: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ── Build Certificate ───────────────────────────────────────────────────

export function useBuildCertificate(investigationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => buildCertificate(investigationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
      queryClient.invalidateQueries({
        queryKey: ['investigation', investigationId, 'readiness'],
      });
      queryClient.invalidateQueries({
        queryKey: ['investigation', investigationId, 'certificate'],
      });
    },
  });
}

// ── Run Admission (Workspace Phase 8 — FORGE admission bridge) ──────────

export function useRunAdmission(investigationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => runAdmission(investigationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigations'] });
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
    },
  });
}

// ── Shadow Bootstrap (Workspace Phase 8b) ────────────────────────────────

export function useShadowBootstrap(investigationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => runShadowBootstrap(investigationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigations'] });
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
    },
  });
}

// ── Shadow Execution (Workspace Phase 8b-next) ──────────────────────────

export function useShadowExecution(investigationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => runShadowExecution(investigationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigations'] });
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
    },
  });
}

// ── Live Promotion (Workspace Phase 9) ───────────────────────────────────

export function useMaterializeTheatre(investigationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => materializeTheatre(investigationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigations'] });
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
    },
  });
}

export function usePromoteLive(investigationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => promoteLive(investigationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigations'] });
      queryClient.invalidateQueries({ queryKey: ['investigation', investigationId] });
    },
  });
}
